import React, { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Divider,
  Table,
  ActionIcon,
  Modal,
  Collapse,
  Paper
} from '@mantine/core';
import { IconEdit, IconChevronDown, IconChevronUp, IconTrash } from '@tabler/icons-react';
import { useClient } from '../../context/ClientContext';
import { useSalon } from '../../context/SalonContext';
import ClientForm from './ClientForm';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { formatDateTime, calculateAge } from '../../utils/timeUtils';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

function ClientDetails({ client, onClose }) {
  const { setSelectedClient, deleteClient } = useClient();
  const { businessInfo } = useSalon();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [expandedAppointments, setExpandedAppointments] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const toggleAppointment = (appointmentId) => {
    setExpandedAppointments(prev => ({
      ...prev,
      [appointmentId]: !prev[appointmentId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'no-show':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteClient(client.id);
      onClose();
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="xl" fw={500}>
          {client.first_name} {client.last_name}
        </Text>
        <Group>
          <Button
            leftSection={<IconEdit size={14} />}
            onClick={() => setIsEditModalOpen(true)}
            variant="light"
          >
            Edit
          </Button>
          <Button
            leftIcon={<IconTrash size={16} />}
            color="red"
            variant="subtle"
            onClick={() => setShowDeleteConfirmation(true)}
          >
            Delete Client
          </Button>
        </Group>
      </Group>

      <Paper p={0}>
        <Stack gap="xs">
          <Group>
            <Text fw={500} w={120}>Phone:</Text>
            <Text>{formatPhoneNumber(`${client.area_code}${client.phone_number}`)}</Text>
          </Group>
          <Group>
            <Text fw={500} w={120}>Email:</Text>
            <Text>{client.email || '-'}</Text>
          </Group>
          <Group>
            <Text fw={500} w={120}>Gender:</Text>
            <Text>{client.gender || '-'}</Text>
          </Group>
          <Group>
            <Text fw={500} w={120}>Pronouns:</Text>
            <Text>{client.pronouns || '-'}</Text>
          </Group>
            <Group>
              <Text fw={500} w={120}>Birthday:</Text>
              {client.birthday ? (
                <Text>
                  {formatDateTime(client.birthday, null, 'MMMM D, YYYY')}
                  {calculateAge(client.birthday) !== null && (
                    <Text component="span" c="dimmed" ml={8}>
                      ({calculateAge(client.birthday)} years old)
                    </Text>
                  )}
                </Text>
              ) : (
                <Text>-</Text>
              )}
            </Group>
          <Group>
            <Text fw={500} w={120}>Opt-in Status:</Text>
            <Group gap="xs">
              <Badge color={client.is_opted_in_reminder ? 'green' : 'red'} variant="light">
                Reminder: {client.is_opted_in_reminder ? 'Yes' : 'No'}
              </Badge>
              <Badge color={client.is_opted_in_marketing ? 'green' : 'red'} variant="light">
                Marketing: {client.is_opted_in_marketing ? 'Yes' : 'No'}
              </Badge>
            </Group>
          </Group>
          {client.client_notes && (
            <Group>
              <Text fw={500} w={120}>Notes:</Text>
              <Text>{client.client_notes}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Divider />

      <Text size="lg" fw={500}>Appointment History</Text>
      
      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Staff</Table.Th>
              <Table.Th>Service</Table.Th>
              <Table.Th>Price</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Payment</Table.Th>
              <Table.Th style={{ width: 50 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {client.appointments?.map(appointment => (
              <React.Fragment key={appointment.id}>
                <Table.Tr>
                  <Table.Td>
                    {formatDateTime(appointment.start_dt, businessInfo?.timezone, 'MMM D, YYYY h:mm A')}
                  </Table.Td>
                  <Table.Td>{appointment.primary_staff_name}</Table.Td>
                  <Table.Td>{appointment.booked_services.map(s => s.name).join(', ')}</Table.Td>
                  <Table.Td>
                    {appointment.final_charged_price ? 
                      `$${appointment.final_charged_price.toFixed(2)}` : '-'}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={getStatusColor(appointment.status)} variant="light">
                      {appointment.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{appointment.payment_method || '-'}</Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => toggleAppointment(appointment.id)}
                    >
                      {expandedAppointments[appointment.id] ? 
                        <IconChevronUp size={14} /> : 
                        <IconChevronDown size={14} />}
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr onClick={() => console.log(appointment)}>
                  <Table.Td colSpan={7} p={0}>
                    <Collapse in={expandedAppointments[appointment.id]}>
                      <Paper p="md" radius={0} bg="gray.0">
                        <Stack gap="xs">
                          <Group>
                            <Text size="sm">Salon Notes:</Text>
                            <Text size="sm">{appointment.salon_notes || '-'}</Text>
                          </Group>
                          <Group>
                            <Text size="sm">Customer Provided Notes:</Text>
                            <Text size="sm">{appointment.customer_notes || '-'}</Text>
                          </Group>
                        </Stack>
                      </Paper>
                    </Collapse>
                  </Table.Td>
                </Table.Tr>
              </React.Fragment>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Client"
        size="lg"
      >
        <ClientForm 
          client={client}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedClient(null);
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Client"
        message={`Are you sure you want to delete ${client.first_name} ${client.last_name}? This action cannot be undone.`}
      />
    </Stack>
  );
}

export default ClientDetails; 